import { FULL_STACK, StackMark, type StackEntry } from "../StackLogo";
import {
  accessRegister,
  clientInvoice,
  clientProfile,
  evidenceLegend,
  infrastructure,
  operatingMetrics,
  workstreams,
} from "../../data/alessandroWorkspace";

const fallback: Record<string, StackEntry> = {
  VPS: { name: "VPS", domain: "hostinger.com", description: "Dedicated client runtime" },
  Codex: { name: "Codex", img: "https://yoqhouniznljdsmpkblf.supabase.co/storage/v1/object/public/portfolio-assets/lobehub-icons/openai.svg", description: "Engineering agent" },
  "GoHighLevel MCP / CLI": { name: "GoHighLevel", domain: "gohighlevel.com", description: "CRM integration" },
  Portainer: { name: "Portainer", domain: "portainer.io", description: "Container management" },
};

function stackEntry(name: string) {
  return FULL_STACK.find((entry) => entry.name === name) ?? fallback[name] ?? { name, description: name };
}

export function ClientHQ() {
  return (
    <section className="intel-view hq-view">
      <div className="hq-command">
        <div className="hq-command-copy">
          <span className="intel-eyebrow">ACTIVE CLIENT PROGRAM · OPERATION 60K</span>
          <div className="hq-title-row">
            <h2>Build the AI operating layer behind AG Coaching.</h2>
            <span className="hq-health"><i /> DISCOVERY COMPLETE</span>
          </div>
          <p>
            Content production, Instagram conversations, Skool nurturing, GoHighLevel qualification and revenue attribution—operated as one measurable system by an Automatrix FDE.
          </p>
          <div className="hq-classification">
            <span><small>CLIENT LEVEL</small><b>{clientProfile.classification}</b></span>
            <span><small>PRIMARY DEMAND</small><b>{clientProfile.demand}</b></span>
            <span><small>CURRENT STAGE</small><b>{clientProfile.stage}</b></span>
            <span><small>PHASE 02</small><b>{clientProfile.initialDemand}</b></span>
          </div>
        </div>
        <aside className="hq-next">
          <span>NEXT CONTROL POINT</span>
          <b>Access & systems audit</b>
          <p>Meta, Instagram, GHL, WAGHL, Skool and the current publishing platform.</p>
          <div><i style={{ width: "22%" }} /></div>
          <small>Project definition · 22%</small>
        </aside>
      </div>

      <div className="hq-metrics">
        {operatingMetrics.map((metric) => (
          <article key={metric.label}>
            <span>{metric.label}</span>
            <b>{metric.value}</b>
            <small>{metric.note}</small>
            <em className={`evidence-${metric.level}`}>CLIENT REPORTED</em>
          </article>
        ))}
      </div>

      <div className="hq-grid">
        <article className="intel-panel hq-workstreams">
          <header className="hq-panel-head">
            <div><span>DELIVERY SYSTEM</span><h3>Six connected workstreams</h3></div>
            <small>Scope evolves from conversations → measured revenue</small>
          </header>
          <div>
            {workstreams.map((stream) => (
              <div className="hq-workstream" key={stream.id}>
                <span>{stream.id}</span>
                <div><h4>{stream.title}</h4><p>{stream.text}</p><div><i style={{ width: `${stream.progress}%` }} /></div></div>
                <strong>{stream.status}</strong>
              </div>
            ))}
          </div>
        </article>

        <div className="hq-side-stack">
          <article className="intel-panel hq-invoice-card">
            <header className="hq-invoice-head">
              <span className="hq-stripe-mark"><img src="https://cdn.simpleicons.org/stripe/635BFF" alt="Stripe" /></span>
              <div><span>PARTNERSHIP INVOICE</span><h3>{clientInvoice.amount}</h3></div>
              <strong>{clientInvoice.status}</strong>
            </header>
            <p>{clientInvoice.description}</p>
            <dl>
              <div><dt>Invoice</dt><dd>{clientInvoice.number}</dd></div>
              <div><dt>Issued</dt><dd>24 Aug 2026</dd></div>
              <div><dt>Due</dt><dd>23 Sep 2026</dd></div>
            </dl>
            <div className="hq-invoice-actions">
              <a href={clientInvoice.hostedUrl} target="_blank" rel="noreferrer">Pay invoice <span>↗</span></a>
              <a href={clientInvoice.pdfUrl} target="_blank" rel="noreferrer">View PDF</a>
            </div>
            <small>Secure payment page hosted by Stripe · email was not sent automatically</small>
          </article>

          <article className="intel-panel hq-contact-card">
            <header className="hq-panel-head"><div><span>CLIENT RECORD</span><h3>Contact & ownership</h3></div></header>
            <div className="hq-contact-person">
              <img src="/client-intelligence/profiles/alessandro-gatti.jpg" alt="Alessandro Gatti" />
              <div><b>{clientProfile.name}</b><span>{clientProfile.handle}</span></div>
              <strong>ICP-A</strong>
            </div>
            <dl>
              <div><dt>Company / offer</dt><dd>{clientProfile.company} · {clientProfile.offer}</dd></div>
              <div><dt>Email</dt><dd><a href={`mailto:${clientProfile.email}`}>{clientProfile.email}</a></dd></div>
              <div><dt>Phone / channel</dt><dd>{clientProfile.phone} · {clientProfile.channel}</dd></div>
              <div><dt>Language / timezone</dt><dd>{clientProfile.language} · {clientProfile.timezone}</dd></div>
              <div><dt>Automatrix owner</dt><dd>{clientProfile.owner}</dd></div>
            </dl>
          </article>

          <article className="intel-panel hq-funnel-card">
            <header className="hq-panel-head"><div><span>MEASUREMENT MODEL</span><h3>One conversion chain</h3></div></header>
            <div className="hq-funnel-chain">
              {[
                ["Follower", "Source + cost"], ["Conversation", "Reply + intent"], ["Qualified", "Fit + urgency"],
                ["Booked", "Calendar + source"], ["Show", "Attendance"], ["Sale", "Collected €"],
              ].map(([label, note], index) => <div key={label}><span>{String(index + 1).padStart(2, "0")}</span><p><b>{label}</b><small>{note}</small></p></div>)}
            </div>
          </article>
        </div>
      </div>

      <div className="hq-grid hq-grid-lower">
        <article className="intel-panel hq-infrastructure">
          <header className="hq-panel-head">
            <div><span>CLIENT SERVER</span><h3>Infrastructure & runtime register</h3></div>
            <small>Credentials remain masked in the client surface</small>
          </header>
          <div className="hq-infra-grid">
            {infrastructure.map((item) => {
              const entry = stackEntry(item.name);
              return <div key={item.name}>
                <i className={item.name === "Codex" ? "stack-codex" : ""}><StackMark entry={entry} /></i>
                <p><b>{item.name}</b><small>{item.detail}</small></p>
                <span className={`infra-${item.state.toLowerCase().replaceAll(" ", "-")}`}>{item.state}</span>
              </div>;
            })}
          </div>
          <div className="hq-secret-row"><span>SERVER URL</span><b>Pending provisioning</b><span>ADMIN USER</span><b>••••••••</b><span>PASSWORD</span><b>••••••••••••</b></div>
        </article>

        <article className="intel-panel hq-access">
          <header className="hq-panel-head"><div><span>INTEGRATION REGISTER</span><h3>Access required to make data live</h3></div></header>
          <div>
            {accessRegister.map((item) => <div key={item.system}>
              <p><b>{item.system}</b><small>{item.purpose}</small></p>
              <span className={`access-${item.status.toLowerCase().replaceAll(" ", "-")}`}>{item.status}</span>
            </div>)}
          </div>
        </article>
      </div>

      <article className="intel-panel hq-evidence-legend">
        <header className="hq-panel-head"><div><span>DATA DISCIPLINE</span><h3>Every number keeps its evidence level</h3></div></header>
        <div>{evidenceLegend.map((item) => <div key={item.level}><i className={`evidence-${item.level}`} /><p><b>{item.label}</b><small>{item.description}</small></p></div>)}</div>
      </article>
    </section>
  );
}
